import { graphql } from 'gatsby';
import React, { useMemo } from 'react';
import { withPreview } from '../utils/withPreview';
import Box from '../components/Box/Box';
import ContentContainer from '../components/ContentContainer/ContentContainer';
import Footer from '../components/Footer/Footer';
import PageWrapper from '../components/PageWrapper/PageWrapper';
import RichText from '../components/RichText/RichText';
import {
    Body1,
    Head1,
    Head2,
    Head4,
    Headline,
} from '../components/Typography/Typography';
import Underline from '../components/Underline/Underline';
import Header from '../components/Header/Header';
import SectionWrapper from '../components/SectionWrapper/SectionWrapper';
import { rem } from 'polished';
import Stack from '../components/Stack/Stack';
import useViewport from '../hooks/useViewport';
import { css } from 'styled-components';
import CollapseContainer from '../components/CollapseContainer/CollapseContainer';
import { theme } from '../../config/theme';
import PostSliceZone from '../components/PostSliceZone/PostSliceZone';

function useScrollSpy(ids: string[], options: IntersectionObserverInit) {
    const [activeId, setActiveId] = React.useState<string>();
    const observer = React.useRef<IntersectionObserver>();

    React.useEffect(() => {
        const elements = ids.map((id) => document.getElementById(id));
        observer.current?.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry?.isIntersecting) {
                    setActiveId(entry.target.id);
                }
            });
        }, options);
        elements.forEach((el) => {
            if (el) {
                observer.current?.observe(el);
            }
        });
        return () => observer.current?.disconnect();
    }, [ids, options]);
    return activeId;
}

const TOC = ({ headings }) => {
    const activeId = useScrollSpy(
        headings.map(({ id }) => id),
        { rootMargin: '0% 0% -75% 0%' }
    );

    return (
        <>
            {headings?.map((heading) => {
                const id = `h-${heading.text
                    .split(' ')
                    .join('-')
                    .toLowerCase()}`;
                return (
                    <Body1
                        // @ts-ignore
                        color={
                            activeId === id ? 'primary.1900' : 'neutral.1100'
                        }
                        fontFamily="heading"
                        css={css`
                            font-weight: ${activeId === id ? 500 : 400};
                            a {
                                border-bottom: initial;
                                display: flex;
                                height: 100%;
                                gap: 8px;
                                position: relative;
                                padding-inline-start: ${activeId === id
                                    ? '12px'
                                    : 0};
                                transition: padding 200ms ease;

                                &::before {
                                    content: '';
                                    display: block;
                                    position: absolute;
                                    transition: transform 200ms ease;
                                    transform: scaleX(
                                        ${activeId === id ? 1 : 0}
                                    );
                                    transform-origin: left left;
                                    width: 4px;
                                    height: 100%;
                                    border-radius: 0 2px 2px 0;
                                    background-color: ${({ theme }) =>
                                        theme.colors.primary.main};
                                    left: 0;
                                    top: 0;
                                }
                            }
                        `}
                    >
                        <a
                            className="toc-link"
                            href={`#${encodeURIComponent(id)}`}
                        >
                            {heading.text}
                        </a>
                    </Body1>
                );
            })}
            <Body1
                fontFamily="heading"
                color="neutral.1100"
                css={css`
                    font-weight: 500;
                `}
            >
                <a className="toc-link" href="#s-post-hero-section">
                    Back to top
                </a>
            </Body1>
        </>
    );
};

const TOCForMobile = ({ headings }) => {
    return (
        <CollapseContainer initiallyOpen={false}>
            <CollapseContainer.Header>
                <Box
                    backgroundColor="primary.50"
                    display="flex"
                    justifyContent="space-between"
                    border={`2px solid ${theme.colors.primary[200]}`}
                    alignItems="center"
                    padding={rem(16)}
                    borderRadius="sm"
                    userSelect="none"
                >
                    <Headline>Table of contents</Headline>
                    <CollapseContainer.Arrow size={24} />
                </Box>
            </CollapseContainer.Header>
            <CollapseContainer.Item>
                <Box
                    px={rem(16)}
                    css={css`
                        h6 + * a {
                            border-top: 2px solid ${theme.colors.primary[50]};
                        }
                    `}
                >
                    {headings.map((heading) => (
                        <Headline color="primary.main">
                            <Box
                                display="block"
                                color="neutral.1100"
                                py={rem(8)}
                                as="a"
                                href={`#h-${heading.text
                                    .replace(/(\r\n|\n|\r)/gm, '')
                                    .split(' ')
                                    .join('-')
                                    .toLowerCase()}`}
                            >
                                {heading.text}
                            </Box>
                        </Headline>
                    ))}
                </Box>
            </CollapseContainer.Item>
        </CollapseContainer>
    );
};

function privacyAndTerms({ data: { page } }) {
    const { body } = page.data;
    const {
        primary: { heading, content, date },
    } = body[0];
    const { isDesktop } = useViewport();

    const headingOfLevel2 = useMemo(
        () =>
            content.richText
                .filter(
                    (text) => text.type === 'heading2' && !!text.text.trim()
                )
                .map((item) => ({
                    ...item,
                    text: item.text.replace(/(\r\n|\n|\r)/gm, ''),
                    id: `h-${item.text
                        .replace(/(\r\n|\n|\r)/gm, '')
                        .split(' ')
                        .join('-')
                        .toLowerCase()}`,
                })),
        [body]
    );

    return (
        <PageWrapper page={page}>
            <Header />
            <main>
                <SectionWrapper>
                    <Box gridColumn={{ _: '1/-1', md: '2/-2', xl: '2/-2' }}>
                        <Box>
                            <Head2 bold as="h1" textAlign={['start', 'center']}>
                                {heading.text}
                            </Head2>
                            <Box
                                marginBottom={['30px', null, '30px', '48px']}
                                marginTop="20px"
                                textAlign={['start', 'center']}
                                fluid
                            >
                                <Body1>
                                    Last updated:{' '}
                                    {new Date(
                                        date || page.last_publication_date
                                    ).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    })}
                                </Body1>
                            </Box>
                        </Box>
                        <Box
                            display="grid"
                            gridColumnGap={rem(24)}
                            gridRowGap={rem(60)}
                            gridTemplateColumns={{ _: 'repeat(10, 1fr)' }}
                        >
                            <Stack
                                as="nav"
                                space={rem(10)}
                                gridColumn={{ _: '1 / -1', lg: '1 / 4' }}
                                gridRow={1}
                                position={{ _: 'static', lg: 'sticky' }}
                                overflowY={{ _: 'initial', lg: 'auto' }}
                                height={{
                                    _: 'auto',
                                    lg: 'calc(100vh - 150px)',
                                }}
                                top="calc(var(--header-height) + 45px)"
                                alignSelf="start"
                            >
                                {!isDesktop ? (
                                    <TOCForMobile headings={headingOfLevel2} />
                                ) : (
                                    <TOC headings={headingOfLevel2} />
                                )}
                            </Stack>
                            <Box
                                backgroundColor="white"
                                borderRadius="8px"
                                paddingBottom={['60px', null, '60px', '82px']}
                                css={css`
                                    img {
                                        max-width: 100%;
                                    }
                                    h2 {
                                        font-size: ${rem(28)};
                                    }
                                    h3 {
                                        font-size: ${rem(24)};
                                    }
                                    h4 {
                                        font-size: ${rem(22)};
                                    }
                                    h5 {
                                        font-size: ${rem(20)};
                                    }
                                    h6 {
                                        font-size: ${rem(18)};
                                    }
                                `}
                                gridColumn={{ _: '1/-1', lg: '4/-1' }}
                                gridRow={{ _: '2', lg: '1' }}
                            >
                                <RichText
                                    underlineLinks
                                    space="0.5em"
                                    render={content.richText}
                                />
                            </Box>
                        </Box>
                    </Box>
                </SectionWrapper>
            </main>
            <Footer />
        </PageWrapper>
    );
}

export const query = graphql`
    query PRIVACY_AND_TERMS_QUERY($uid: String!, $lang: String!) {
        page: prismicPage(uid: { eq: $uid }, lang: { eq: $lang }) {
            _previewable
            lang
            last_publication_date
            data {
                ...PAGE_META_FRAGMENT
                body {
                    ... on PrismicPageDataBodyTermsPrivacyPage {
                        id
                        primary {
                            heading {
                                text
                            }
                            subheading
                            content {
                                richText
                            }
                            date
                        }
                    }
                }
            }
        }
    }
`;

export default withPreview(privacyAndTerms);
